import { useRef } from 'react'
import { Button } from '@/components/Button'
import { useSwipe } from '@/hooks/useSwipe'

interface ImageGalleryProps {
	images: string[]
	/** Names the subject of the images in their alt text, e.g. "Unit 101". */
	label?: string
	currentIndex?: number
	onIndexChange?: (index: number) => void
	/** Opens the full gallery on click, Enter or Space. */
	onOpen?: () => void
	hasVirtualTour?: boolean
	onVirtualTour?: () => void
	className?: string
}

export function ImageGallery ({
	images,
	label,
	currentIndex = 0,
	onIndexChange,
	onOpen,
	hasVirtualTour = false,
	onVirtualTour,
	className = ''
}: ImageGalleryProps) {
	const total = images.length
	const rootRef = useRef<HTMLDivElement>(null)

	const goPrev = () => onIndexChange?.(currentIndex > 0 ? currentIndex - 1 : total - 1)
	const goNext = () => onIndexChange?.(currentIndex < total - 1 ? currentIndex + 1 : 0)

	useSwipe(rootRef, {
		onSwipeLeft: goNext,
		onSwipeRight: goPrev,
		enabled: total > 1
	})

	const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
		// Only the container itself — Enter on the inner buttons must keep activating them.
		if (onOpen && e.target === e.currentTarget && (e.key === 'Enter' || e.key === ' ')) {
			e.preventDefault()
			onOpen()
			return
		}
		if (total <= 1) return
		if (e.key === 'ArrowLeft') {
			e.preventDefault()
			goPrev()
		} else if (e.key === 'ArrowRight') {
			e.preventDefault()
			goNext()
		}
	}

	return (
		<div
			ref={rootRef}
			role='group'
			aria-label={`Image ${currentIndex + 1} of ${total}`}
			tabIndex={total > 1 || onOpen ? 0 : undefined}
			onKeyDown={handleKeyDown}
			onClick={onOpen}
			className={`spaces-image-gallery spaces:relative spaces:aspect-[16/9] spaces:w-full spaces:rounded-16 spaces:overflow-hidden spaces:bg-image-bg spaces:border spaces:border-canvas-3 ${onOpen ? 'spaces:cursor-pointer' : ''} ${className}`}
		>
			{images[currentIndex] && (
				<img
					src={images[currentIndex]}
					alt={`${label || 'Gallery'} image ${currentIndex + 1} of ${total}`}
					draggable={false}
					className='spaces:absolute spaces:inset-0 spaces:w-full spaces:h-full spaces:object-contain'
				/>
			)}

			{/* Gallery indicator */}
			{total > 1 && (
				<div aria-hidden='true' className='spaces-image-gallery-counter spaces:absolute spaces:bottom-8 spaces:right-8 spaces:flex spaces:items-center spaces:gap-4 spaces:px-12 spaces:py-8 spaces:rounded-8 spaces:bg-black/55 spaces:backdrop-blur-default spaces:text-14/16 spaces:font-default'>
					<span className='spaces:text-white'>{currentIndex + 1}</span>
					<span className='spaces:text-white/50'>/</span>
					<span className='spaces:text-white'>{total}</span>
				</div>
			)}

			{/* Virtual tour button */}
			{hasVirtualTour && (
				<Button
					iconOnly='Tour360'
					appearance='stroke'
					color='content4'
					size='md'
					aria-label='Virtual Tour'
					onClick={e => {
						e.stopPropagation()
						onVirtualTour?.()
					}}
					className='spaces-image-gallery-tour spaces:absolute spaces:bottom-8 spaces:left-8 spaces:shadow-xs-below'
				/>
			)}

			{/* Navigation arrows */}
			{total > 1 && (
				<>
					<Button
						iconOnly='ArrowLeft'
						appearance='stroke'
						color='content4'
						size='md'
						aria-label='Previous image'
						onClick={e => {
							e.stopPropagation()
							goPrev()
						}}
						className='spaces-image-gallery-prev spaces:absolute spaces:left-8 spaces:top-1/2 spaces:-translate-y-1/2'
					/>
					<Button
						iconOnly='ArrowRight'
						appearance='stroke'
						color='content4'
						size='md'
						aria-label='Next image'
						onClick={e => {
							e.stopPropagation()
							goNext()
						}}
						className='spaces-image-gallery-next spaces:absolute spaces:right-8 spaces:top-1/2 spaces:-translate-y-1/2'
					/>
				</>
			)}
		</div>
	)
}
